import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, Container, Row, Col, Card } from 'react-bootstrap';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import ConfirmationModal from '../shared/ConfirmationModal';
import NewConfirmationModal from '../shared/NewconfirmationModal'; // Using existing modal
import AllFailureModal from '../shared/AllFailureModal'; // Using existing modal
import isEqual from 'lodash/isEqual';
import loaderimage from '../../images/giphy.gif';


type NoticeFormValues = {
  job_id: string;
  title: string;
  description: string;
  audience: string;
  posted_by: string;
  expiry_date: string;
  notice_id?: string;
};

const NoticeUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [initialValues, setInitialValues] = useState<NoticeFormValues>({
    job_id: '',
    title: '',
    description: '',
    audience: '',
    posted_by: '',
    expiry_date: '',
    notice_id: '',
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [modalShow, setModalShow] = useState(false);
    const [showSuccessModal, setShowSuccessModal] = useState(false);
      const [successMessage, setSuccessMessage] = useState("");
      const [failureMessage, setFailureMessage] = useState("");
  const [failureModalShow, setFailureModalShow] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [pendingValues, setPendingValues] = useState<NoticeFormValues | null>(null);
  const [operationType, setOperationType] = useState<'Update' | 'Delete' | 'Yes'>('Update');


  useEffect(() => {
    if (id) {
      const fetchNotice = async () => {
        try {
          setLoading(true);
          const response = await axios.get(`${process.env.REACT_APP_API_URL}notice/noticebyid/${id}`);
          if (response.data && response.data[0]) {
            const fetchedNotice = {
              ...response.data[0],
              expiry_date: response.data[0].expiry_date.split('T')[0],
            };
            setInitialValues(fetchedNotice);
          }
        } catch (error) {
          console.error('Error fetching notice:', error);
          setModalMessage('Failed to load notice data.');
          setFailureModalShow(true);
        } finally {
          setLoading(false);
        }
      };
      fetchNotice();
    }
  }, [id]);


  const validationSchema = Yup.object({
    job_id: Yup.string().required('Job ID is required'),
    title: Yup.string().required('Title is required'),
    description: Yup.string().required('Description is required'),
    audience: Yup.string().required('Audience is required'),
    posted_by: Yup.string().required('Posted by is required'),
    expiry_date: Yup.date().required('Expiry Date is required').nullable(),
  });

  const handleUpdateConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed && pendingValues) {
      try {
        const response = await axios.put(`${process.env.REACT_APP_API_URL}notice/updatenotice/${id}`, pendingValues);
        if (response.status === 200) {
          setSuccessMessage('Notice updated successfully');
          setShowSuccessModal(true);
          setTimeout(() => {
          navigate('/noticelist');
          }, 3000);
        }
      } catch (error) {
        console.log('Error updating notice:', error);
        setFailureMessage('Failed to update notice.');
        setFailureModalShow(true);
      }
    } else {
      setFailureMessage('Update canceled.');
      setFailureModalShow(true);
    }
  };

  const handleDeleteConfirmation = async (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed) {
      try {
        const response = await axios.delete(`${process.env.REACT_APP_API_URL}notice/deletenotice/${id}`);
        if (response.status === 200) {
          setSuccessMessage('Notice deleted successfully');
          setShowSuccessModal(true);
          setTimeout(() => {
          navigate('/noticelist');
          }, 3000);
        }
      } catch (error) {
        console.log('Error deleting notice:', error);
        setFailureMessage('Failed to delete notice.');
        setFailureModalShow(true);
      }
    } else {
      setFailureMessage('Delete canceled.');
      setFailureModalShow(true);
    }
  };

  const handleCancelConfirmation = (isConfirmed: boolean) => {
    setModalShow(false);
    if (isConfirmed) {
      navigate('/noticelist');
    } else {
      setModalMessage('Cancel operation aborted.');
      setModalShow(true);
    }
  };

  return (
    <>
      <Container  className="mt-5">
      <Row className="justify-content-center">
          <Col md={8}>
            <Card className="shadow">
              <Card.Header className="bg-info text-white text-center">
                <h4>Update Notice</h4>
              </Card.Header>
              <Card.Body style={{ maxHeight: '60vh', overflowY: 'auto' }}>
              {loading ? (
                  <div className="text-center m-3">
                    <img src={loaderimage} alt="Loading..." className="img-fluid" style={{ height: "50px" }} />
                  </div>
                ) : (
        <Formik
          initialValues={initialValues}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (isEqual(values, initialValues)) {
              setModalMessage('No changes were made.');
              setModalShow(true);
            } else {
              setPendingValues(values); // Save values for later confirmation
              setModalMessage('Are you sure you want to update the notice?');
              setOperationType('Update');
              setModalShow(true);
            }
          }}
        >
          {({ isSubmitting, isValid, dirty }) => (
            <Form>
              <Row className="mb-3">
                <Col md={6}>
                  
                    <label htmlFor="job_id" className="form-label">Job ID: </label>
                    <Field
                      type="text"
                      id="job_id"
                      name="job_id"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="job_id"
                      component="div"
                      className="text-danger"
                    />
                  
                </Col>
                <Col md={6}>
                  
                    <label htmlFor="title" className="form-label">Title: </label>
                    <Field
                      type="text"
                      id="title"
                      name="title"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="title"
                      component="div"
                      className="text-danger"
                    />
                  
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={12}>
                  
                    <label htmlFor="description" className="form-label">Description: </label>
                    <Field
                      as="textarea"
                      id="description"
                      name="description"
                      className="form-control"
                      rows={4}
                    />
                    <ErrorMessage
                      name="description"
                      component="div"
                      className="text-danger"
                    />
                  
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  
                    <label htmlFor="audience"  className="form-label">Audience: </label>
                    <Field
                      type="text"
                      id="audience"
                      name="audience"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="audience"
                      component="div"
                      className="text-danger"
                    />
                  
                </Col>
                <Col md={6}>
                  
                    <label htmlFor="posted_by"  className="form-label">Posted By: </label>
                    <Field
                      type="text"
                      id="posted_by"
                      name="posted_by"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="posted_by"
                      component="div"
                      className="text-danger"
                    />
                  
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  
                    <label htmlFor="expiry_date" className="form-label">Expiry Date: </label>
                    <Field
                      type="date"
                      id="expiry_date"
                      name="expiry_date"
                      className="form-control"
                    />
                    <ErrorMessage
                      name="expiry_date"
                      component="div"
                      className="text-danger"
                    />
                
                </Col>
              </Row>
              
              <Row className="mt-4">
                        <Col className="text-center">
                       
              <Button type="submit" variant="success" disabled={isSubmitting || !isValid || !dirty}>
                Update Notice
              </Button>

              <Button
                variant="danger"
                className="mx-2"
                onClick={() => navigate('/noticelist')}
              >
                Cancel
              </Button>

              {/* <Button variant="danger" className="mx-2" onClick={() => navigate('/noticelist')}>
                            Go Back
                          </Button> */}

              
              </Col>
                      </Row>
                    </Form>
                  )}
                </Formik>
              )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ConfirmationModal
        show={modalShow}
        modalMessage={modalMessage}
        onHide={() => setModalShow(false)}
        confirmation={operationType === 'Update' ? handleUpdateConfirmation : operationType === 'Delete' ? handleDeleteConfirmation : handleCancelConfirmation}
        operationType={operationType}
      />
      <NewConfirmationModal
        show={showSuccessModal}
        modalMessage={successMessage}
        onHide={() => setShowSuccessModal(false)}
        confirmation={handleUpdateConfirmation}
        operationType={'OK'}
      />
      <AllFailureModal
      show={failureModalShow}
      modalMessage={modalMessage}
      onHide={() => setFailureModalShow(false)}
      />
    </>
  );
};

export default NoticeUpdate;
